import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/CloseRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import axios from 'axios';
import endpoints, { getBackendUrl } from '../../../endpoints';
import { useSelector } from 'react-redux'
import Loading from '../parts/Loading'

function formatDate(value) {
    if (!value) {
        return null
    }
    // Upwork employment/education dates come back as YYYY-MM-DD.
    const d = new Date(value)
    if (isNaN(d.getTime())) {
        return value
    }
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' })
}

function formatWorkDate(value) {
    if (!value) {
        return null
    }
    const d = new Date(value)
    if (isNaN(d.getTime())) {
        return value
    }
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatMoney(value) {
    if (value == null || value === '') {
        return null
    }
    const n = Number(value)
    if (!Number.isFinite(n)) {
        return String(value)
    }
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function formatHours(value) {
    if (value == null || value === '') {
        return null
    }
    const n = Number(value)
    if (!Number.isFinite(n)) {
        return String(value)
    }
    return (Math.round(n * 100) / 100) + ' hrs'
}

function isPrivateJob(visibility) {
    return String(visibility || '').toUpperCase() === 'PRIVATE'
}

function groupJobCategories(rows) {
    const groups = []
    const indexByCategory = new Map()
    for (const row of rows ?? []) {
        const category = row.category || 'Other'
        if (!indexByCategory.has(category)) {
            indexByCategory.set(category, groups.length)
            groups.push({ category, subcategories: [] })
        }
        if (row.subcategory) {
            groups[indexByCategory.get(category)].subcategories.push(row.subcategory)
        }
    }
    return groups
}

function contractRateLabel(item) {
    const type = String(item.job_type || '').toUpperCase()
    if (type === 'HOURLY' && item.hourly_rate != null) {
        return formatMoney(item.hourly_rate) + '/hr'
    }
    if ((type === 'FIXED' || type === 'FIXED_PRICE') && item.fixed_amount != null) {
        return formatMoney(item.fixed_amount) + ' fixed'
    }
    if (item.hourly_rate != null) {
        return formatMoney(item.hourly_rate) + '/hr'
    }
    if (item.fixed_amount != null) {
        return formatMoney(item.fixed_amount) + ' fixed'
    }
    return type || null
}

export default function TeamMemberModal(props) {
    const [member, setMember] = useState(null)
    const [loading, setLoading] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const token = useSelector((state) => state.auth.token)

    const showAlertError = (message) => { props.showAlert(message, 'error') }
    const showAlertSuccess = (message) => { props.showAlert(message, 'success') }

    const load = async () => {
        if (!props.userId) return
        setLoading(true)
        try {
            let uri = getBackendUrl(endpoints.team_member, token).replace('{id}', props.userId)
            let res = await axios.get(uri)
            setMember(res.data.member)
        } catch (e) {
            showAlertError('Unknown Error - get team member')
        }
        setLoading(false)
    }

    useEffect(() => {
        if (props.userId) {
            load()
        } else {
            setMember(null)
        }
    }, [props.userId]);

    const syncHandle = async () => {
        setSyncing(true)
        try {
            let uri = getBackendUrl(endpoints.team_member_sync, token).replace('{id}', props.userId)
            let res = await axios.post(uri)
            setMember(res.data.member)
            showAlertSuccess('Synced from Upwork successfully!')
            props.onSynced()
        } catch (e) {
            showAlertError(e.response?.data?.message || 'Unknown Error - sync team member')
        }
        setSyncing(false)
    }

    const setVisibilityHandle = async (hidden) => {
        const updated = await props.onSetVisibility(props.userId, hidden)
        if (updated) {
            setMember(updated)
        }
    }

    // Bio/skills/employment come from the public profile lookup and stay empty
    // when the freelancer hid their Upwork profile. Work history does not.
    const hasPublicProfile = member && (member.title || member.bio || member.job_success_score != null
        || member.skills?.length || member.job_categories?.length
        || member.employment?.length || member.education?.length || member.portfolio?.length)
    const hasWorkHistory = Boolean(member?.work_history?.length)

    const jobCategoryGroups = groupJobCategories(member?.job_categories)

    return (
        <Dialog open={Boolean(props.userId)} onClose={props.onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={member?.photo_url} sx={{ width: 40, height: 40 }}>
                        {(member?.name || '?').charAt(0)}
                    </Avatar>
                    <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                        <div>{member?.name}</div>
                        {member?.title ?
                            <Typography variant="body2" color="text.secondary" noWrap>{member.title}</Typography>
                        : null}
                    </Stack>
                    <IconButton onClick={props.onClose}><CloseIcon /></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {!member ? null :
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {member.is_hidden ?
                            <Chip label="Hidden" color="warning" />
                        : null}
                        {member.job_success_score != null ?
                            <Chip size="small" label={'Job Success: ' + Math.round(member.job_success_score * 100) + '%'} />
                        : null}
                        {member.top_rated_status && member.top_rated_status !== 'none' ?
                            <Chip size="small" color="success" label={member.top_rated_status.replace('_', ' ')} />
                        : null}
                        {member.total_hourly_jobs != null || member.total_fixed_jobs != null ?
                            <Chip size="small" label={((member.total_hourly_jobs ?? 0) + (member.total_fixed_jobs ?? 0)) + ' jobs on Upwork'} />
                        : null}
                        {member.activation_status !== 1 ?
                            <Chip size="small" label={'Status ' + member.activation_status} color="default" />
                        : null}
                    </Stack>

                    {member.upwork_created_at ?
                        <Typography variant="body2" color="text.secondary">
                            Joined the agency: {new Date(member.upwork_created_at).toLocaleDateString()}
                        </Typography>
                    : null}

                    {member.public_url ?
                        <Typography variant="body2">
                            <a href={member.public_url} target="_blank" rel="noreferrer">View Upwork profile</a>
                        </Typography>
                    : null}

                    {!hasPublicProfile && !hasWorkHistory ?
                        <Chip size="small" label="Profile details unavailable (private Upwork profile)" color="default" />
                    : !hasPublicProfile && hasWorkHistory ?
                        <Chip size="small" label="Public Upwork profile is hidden — bio and skills are unavailable" color="default" />
                    : null}

                    {member.title || member.bio ?
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2">Bio / Overview</Typography>
                            {member.title ?
                                <Typography variant="body2" fontWeight="bold">{member.title}</Typography>
                            : null}
                            {member.bio ?
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{member.bio}</Typography>
                            : null}
                        </Stack>
                    : null}

                    {member.skills?.length ?
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2">Skills</Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {member.skills.map((skill, i) => (
                                    <Chip key={i} size="small" label={skill.name} />
                                ))}
                            </Stack>
                        </Stack>
                    : null}

                    {jobCategoryGroups.length ?
                        <Stack spacing={1}>
                            <Typography variant="subtitle2">Job Categories</Typography>
                            {jobCategoryGroups.map((group) => (
                                <Stack key={group.category} spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">{group.category}</Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        {group.subcategories.length ?
                                            group.subcategories.map((name) => (
                                                <Chip key={name} size="small" variant="outlined" label={name} />
                                            ))
                                        :
                                            <Chip size="small" variant="outlined" label={group.category} />
                                        }
                                    </Stack>
                                </Stack>
                            ))}
                        </Stack>
                    : null}

                    {member.work_history?.length ?
                        <Stack spacing={0.5}>
                            <Divider />
                            <Typography variant="subtitle2" sx={{ pt: 1 }}>
                                Work History ({member.work_history.length})
                            </Typography>
                            <Stack>
                                {member.work_history.map((item, i) => {
                                    const rate = contractRateLabel(item)
                                    const earned = formatMoney(item.total_charge)
                                    const hours = formatHours(item.hours)
                                    const dates = [formatWorkDate(item.start_at), item.end_at ? formatWorkDate(item.end_at) : 'Present'].filter(Boolean).join(' — ')
                                    const privateJob = isPrivateJob(item.job_visibility)
                                    const hasJobDetails = Boolean(item.job_description || item.job_skills?.length)

                                    return (
                                        <Accordion key={item.upwork_contract_id || i} disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Stack sx={{ minWidth: 0, pr: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {item.title || item.job_title || 'Untitled contract'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {[
                                                            dates,
                                                            earned,
                                                            rate,
                                                            hours,
                                                            item.feedback_score != null ? item.feedback_score + '/5' : null,
                                                        ].filter(Boolean).join(' · ')}
                                                    </Typography>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={1}>
                                                    {item.feedback_comment ?
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {item.feedback_comment}
                                                        </Typography>
                                                    : null}
                                                    {item.feedback_scores?.length ?
                                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                            {item.feedback_scores.map((score, si) => (
                                                                <Chip
                                                                    key={si}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={(score.label || 'Score') + ': ' + (score.score ?? '—')}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    : null}

                                                    {privateJob ?
                                                        <Typography variant="caption" color="text.secondary">
                                                            Job posting is private — title, description and skills are not available.
                                                        </Typography>
                                                    : hasJobDetails ?
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="caption" color="text.secondary">Job posting</Typography>
                                                            {item.job_title && item.job_title !== item.title ?
                                                                <Typography variant="body2" fontWeight="bold">{item.job_title}</Typography>
                                                            : null}
                                                            {item.job_description ?
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.job_description}</Typography>
                                                            : null}
                                                            {item.job_skills?.length ?
                                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                                    {item.job_skills.map((skill) => (
                                                                        <Chip key={skill} size="small" label={skill} />
                                                                    ))}
                                                                </Stack>
                                                            : null}
                                                        </Stack>
                                                    : null}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                })}
                            </Stack>
                        </Stack>
                    : null}

                    {member.portfolio?.length ?
                        <Stack spacing={0.5}>
                            <Divider />
                            <Typography variant="subtitle2" sx={{ pt: 1 }}>
                                Portfolio ({member.portfolio.length})
                            </Typography>
                            <Stack>
                                {member.portfolio.map((item, i) => (
                                    <Accordion key={item.upwork_project_id || i} disableGutters>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {item.title || 'Untitled project'}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={1}>
                                                {item.description ?
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {item.description}
                                                    </Typography>
                                                : null}
                                                {item.skills?.length ?
                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                        {item.skills.map((skill) => (
                                                            <Chip key={skill} size="small" label={skill} />
                                                        ))}
                                                    </Stack>
                                                : null}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        </Stack>
                    : null}

                    {member.employment?.length ?
                        <Stack spacing={0.5}>
                            <Divider />
                            <Typography variant="subtitle2" sx={{ pt: 1 }}>Employment History</Typography>
                            <Stack spacing={1.5}>
                                {member.employment.map((e, i) => (
                                    <Stack key={i}>
                                        <Typography variant="body2" fontWeight="bold">
                                            {e.job_title}{e.company_name ? ' · ' + e.company_name : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {[formatDate(e.start_date), e.end_date ? formatDate(e.end_date) : 'Present'].filter(Boolean).join(' — ')}
                                        </Typography>
                                        {e.description ?
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{e.description}</Typography>
                                        : null}
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    : null}

                    {member.education?.length ?
                        <Stack spacing={0.5}>
                            <Divider />
                            <Typography variant="subtitle2" sx={{ pt: 1 }}>Education</Typography>
                            <Stack spacing={1.5}>
                                {member.education.map((e, i) => (
                                    <Stack key={i}>
                                        <Typography variant="body2" fontWeight="bold">
                                            {e.institution_name}{e.degree ? ' · ' + e.degree : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {[formatDate(e.start_date), formatDate(e.end_date)].filter(Boolean).join(' — ')}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    : null}

                    <Divider />
                    <Typography variant="caption" color="text.secondary">
                        {member.synced_at ? 'Last synced from Upwork: ' + new Date(member.synced_at).toLocaleString() : 'Never synced from Upwork'}
                    </Typography>
                </Stack>
                }
                {loading ? <Loading/> : null}
            </DialogContent>
            <DialogActions>
                {props.isAdmin ?
                    <Button startIcon={<SyncRoundedIcon />} onClick={syncHandle} disabled={syncing || !member}>
                        {syncing ? 'Syncing...' : 'Sync now'}
                    </Button>
                : null}
                {props.isAdmin && member ?
                    (member.is_hidden ?
                        <Button startIcon={<VisibilityIcon />} onClick={() => { setVisibilityHandle(false) }}>
                            Show profile
                        </Button>
                    :
                        <Button startIcon={<VisibilityOffIcon />} onClick={() => { setVisibilityHandle(true) }}>
                            Hide profile
                        </Button>
                    )
                : null}
                <Stack sx={{ flexGrow: 1 }} />
                <Button onClick={props.onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
